import { SearchType } from "Dashboard/domain/SearchType";
import ProjectTypeSelectCreate from "Dashboard/scenes/Projects/components/ProjectTypeSelectCreate";
import ProjectCategorySelect from "Dashboard/scenes/Projects/components/ProjectCategorySelect";
import { IGetProjectsListRequestParams } from "Dashboard/scenes/Projects/domain/IGetProjectsListRequestParams";
import { useAppDispatch } from "hooks/hooks";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { FormatDateIso } from "utils/dateUtils";
import { getProjectsList, selectState, setSearchProjectsPopoverOpen } from "../main.slice";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesProjects from "../../../styles/popover.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IGetProjectsListRequestParams>;
  register: UseFormRegister<IGetProjectsListRequestParams>;
  control: Control<IGetProjectsListRequestParams, any>;
  reset: UseFormReset<IGetProjectsListRequestParams>;
  setFocus: UseFormSetFocus<IGetProjectsListRequestParams>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  getValues: UseFormGetValues<IGetProjectsListRequestParams>;
  setValue: UseFormSetValue<IGetProjectsListRequestParams>;
};

const SearchProjectsPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { permission } = useSelector(selectState);
  const {
    register,
    handleSubmit,
    reset,
    control,
    setFocus,
    getValues,
    setValue } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("contractDateFrom") ? new Date(getValues("contractDateFrom")!) : null,
    getValues("contractDateTo") ? new Date(getValues("contractDateTo")!) : null]);

  const [deadlineDateRange, setDeadlineDateRange] = useState<[Date | null, Date | null]>(
    [getValues("deadlineForCompletionFrom") ? new Date(getValues("deadlineForCompletionFrom")!) : null,
    getValues("deadlineForCompletionTo") ? new Date(getValues("deadlineForCompletionTo")!) : null]);

  const [startDateRange, setStartDateRange] = useState<[Date | null, Date | null]>(
    [getValues("startDateFrom") ? new Date(getValues("startDateFrom")!) : null,
    getValues("startDateTo") ? new Date(getValues("startDateTo")!) : null]);

  const [finalDateRange, setFinalDateRange] = useState<[Date | null, Date | null]>(
    [getValues("finalDeadlineFrom") ? new Date(getValues("finalDeadlineFrom")!) : null,
    getValues("finalDeadlineTo") ? new Date(getValues("finalDeadlineTo")!) : null]);

  const [finishDateRange, setFinishDateRange] = useState<[Date | null, Date | null]>(
    [getValues("finishDateFrom") ? new Date(getValues("finishDateFrom")!) : null,
    getValues("finishDateTo") ? new Date(getValues("finishDateTo")!) : null]);

  const close = () => {
    dispatch(setSearchProjectsPopoverOpen({ isOpen: false }));
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchType(searchType);
    reset();
  };

  const onSubmit = async (data: IGetProjectsListRequestParams) => {
    try {
      dispatch(getProjectsList({ permission, data }));
      close();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (props.searchType === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("name");
    }
  }, [setFocus, props.searchType]);


  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`${classesProjects.searchPopover} ${classes.basicPopover}`}
    >
      {props.searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text"
                placeholder=""
                className={`${classes.itemBorder} ${classes.fullWidth}`}
                {...register("searchTerm")}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv projekta</label>
              <input type="text" className={classes.itemBorder}
                {...register("name")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>kategorija</label>
              <Controller
                name="category"
                control={control}
                defaultValue={undefined}
                render={({ field: { onChange } }) => (
                  <ProjectCategorySelect
                    value={getValues("category")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>vrsta</label>
              <Controller
                name="type"
                control={control}
                defaultValue={undefined}
                render={({ field: { onChange } }) => (
                  <ProjectTypeSelectCreate
                    isValidNewOption={false}
                    value={getValues("type")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mesto</label>
              <input type="text" className={classes.itemBorder}
                {...register("municipality")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naručilac</label>
              <input type="text" className={classes.itemBorder}
                {...register("client")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>vrednost projekta</label>
              <input type="text" className={classes.itemBorder}
                {...register("projectValue")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum ugovora od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("contractDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("contractDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>rok za završetak od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={deadlineDateRange[0]}
                  endDate={deadlineDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDeadlineDateRange([date[0], date[1]]);
                    setValue("deadlineForCompletionFrom", date[0] && FormatDateIso(date[0]));
                    setValue("deadlineForCompletionTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>početak radova od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={startDateRange[0]}
                  endDate={startDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setStartDateRange([date[0], date[1]]);
                    setValue("startDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("startDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>krajnji rok od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={finalDateRange[0]}
                  endDate={finalDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setFinalDateRange([date[0], date[1]]);
                    setValue("finalDeadlineFrom", date[0] && FormatDateIso(date[0]));
                    setValue("finalDeadlineTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dana ostalo</label>
              <input type="text" className={classes.itemBorder}
                {...register("daysLeft")} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>završio od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={finishDateRange[0]}
                  endDate={finishDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setFinishDateRange([date[0], date[1]]);
                    setValue("finishDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("finishDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>

              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchProjectsPopover;
